import { ROUTES } from "@/constants/routes";
import { Authority } from "@/types/auth";
import { envUtils } from "@/lib/env";

export interface NavItem {
  label: string;
  route: string;
  description?: string;
  /** Single authority required to view this nav item */
  authority?: Authority;
  /** Multiple authorities - user needs ANY of these */
  anyOf?: Authority[];
  /** Multiple authorities - user needs ALL of these */
  allOf?: Authority[];
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

const ALL_NAV_GROUPS: NavGroup[] = [
  {
    label: "Campaigns",
    items: [
      {
        label: "Recruitments",
        route: ROUTES.PRESCREENING,
        description: "Review Recruitments Details",
        authority: "CAMPAIGN_CANDIDATE_OUTREACH_VIEW",
      },
      {
        label: "TPMO",
        route: ROUTES.CAMPAIGN_TPMO,
        description: "Manage TPMO work orders",
        authority: "CAMPAIGN_TPMO_VIEW",
      }
    ],
  },
  {
    label: "Analytics",
    items: [
      {
        label: "Transaction Tracking",
        route: ROUTES.ANALYTICS_TRANSACTION_TRACKING,
        description: "View and download transaction tracking data",
        authority: "ANALYTICS_TRANSACTION_TRACK_VIEW",
      },
      {
        label: "Failure Transactions",
        route: ROUTES.ANALYTICS_FAILURE_TRANSACTIONS,
        description: "View and analyze failed transaction retry queue data",
        authority: "ANALYTICS_FAILURE_TRANSACTIONS_VIEW",
      },
    ],
  },
  {
    label: "Admin Center",
    items: [
      {
        label: "Campaign Management",
        route: ROUTES.CAMPAIGN_MANAGEMENT,
        description: "Create and manage campaigns",
        authority: "ADMIN_CAMPAIGN_MGMT_VIEW",
      },
      {
        label: "Master Data",
        route: ROUTES.SUPPLEMENT_DATA,
        description: "Manage master data",
        authority: "ADMIN_MASTER_DATA_VIEW",
      },
      {
        label: "Sender Persona",
        route: ROUTES.SENDER_PERSONA,
        description: "Manage sender personas",
        authority: "ADMIN_SENDER_PERSONA_VIEW",
      },
      {
        label: "KnowledgeBase Mgmt.",
        route: ROUTES.KNOWLEDGE_BASE_SETUP,
        description: "Configure knowledge base settings",
        authority: "ADMIN_KNOWLEDGE_MGMT_VIEW",
      },
      {
        label: "Phone Number Mapping",
        route: ROUTES.COMMUNICATION_INTEGRATION,
        description: "Manage communication integrations for different platforms",
        authority: "ADMIN_PHONE_MGMT_VIEW",
      },
      {
        label: "Compliance & Quiet Hours",
        route: ROUTES.COMPLIANCE_QUIET_HOURS,
        description: "Manage country-specific working hours and compliance settings",
        authority: "ADMIN_COUNTRY_MGMT_VIEW",
      },
      {
        label: "Constants Management",
        route: ROUTES.CONSTANTS_MANAGEMENT,
        description: "Manage system constant messages and configurations",
        authority: "ADMIN_CONSTANTS_MGMT_VIEW",
      },
      {
        label: "Role Management",
        route: ROUTES.ROLE_MANAGEMENT,
        description: "Manage user roles and authorities",
        authority: "ADMIN_ROLE_MGMT_VIEW",
      }
    ],
  },
  {
    label: "Testing",
    items: [
      {
        label: "Candidate Mgmt",
        route: ROUTES.TESTING_CANDIDATE_MGMT,
        authority: "ADMIN_TESTING_VIEW",
      },
      {
        label: "Job Order Mgmt",
        route: ROUTES.TESTING_JOB_ORDER_MGMT,
        authority: "ADMIN_TESTING_VIEW",
      },
      {
        label: "View SQS Data",
        route: ROUTES.TESTING_VIEW_SQS,
        description: "View SQS queue data",
        authority: "ADMIN_TESTING_VIEW",
      },
    ],
  }
];

const ALL_MOBILE_NAV_GROUPS: NavGroup[] = [
  {
    label: "Master Data Management",
    items: [
      {
        label: "Master Data",
        route: ROUTES.SUPPLEMENT_DATA,
        authority: "ADMIN_MASTER_DATA_VIEW",
      },
      {
        label: "Sender Persona",
        route: ROUTES.SENDER_PERSONA,
        authority: "ADMIN_SENDER_PERSONA_VIEW",
      },
      {
        label: "Phone Number Mapping",
        route: ROUTES.COMMUNICATION_INTEGRATION,
        authority: "ADMIN_PHONE_MGMT_VIEW",
      },
    ],
  },
  {
    label: "Campaign",
    items: [
      {
        label: "Campaign Management",
        route: ROUTES.CAMPAIGN_MANAGEMENT,
        authority: "ADMIN_CAMPAIGN_MGMT_VIEW",
      },
      {
        label: "TPMO",
        route: ROUTES.CAMPAIGN_TPMO,
        authority: "CAMPAIGN_TPMO_VIEW",
      },
      {
        label: "Campaign Prescreening",
        route: ROUTES.PRESCREENING,
        authority: "CAMPAIGN_CANDIDATE_OUTREACH_VIEW",
      },
    ],
  },
  {
    label: "Analytics",
    items: [
      {
        label: "Transaction Tracking",
        route: ROUTES.ANALYTICS_TRANSACTION_TRACKING,
        authority: "ANALYTICS_TRANSACTION_TRACK_VIEW",
      },
      {
        label: "Failure Transactions",
        route: ROUTES.ANALYTICS_FAILURE_TRANSACTIONS,
        authority: "ANALYTICS_FAILURE_TRANSACTIONS_VIEW",
      },
    ],
  },
  {
    label: "Testing",
    items: [
      {
        label: "Candidate Mgmt",
        route: ROUTES.TESTING_CANDIDATE_MGMT,
        authority: "ADMIN_TESTING_VIEW",
      },
      {
        label: "Job Order Mgmt",
        route: ROUTES.TESTING_JOB_ORDER_MGMT,
        authority: "ADMIN_TESTING_VIEW",
      },
      {
        label: "View SQS Data",
        route: ROUTES.TESTING_VIEW_SQS,
        authority: "ADMIN_TESTING_VIEW",
      },
    ],
  }
];


/**
 * Filter out Testing menu in production environment
 */
function filterNavGroups(groups: NavGroup[]): NavGroup[] {
  if (envUtils.isProduction()) {
    return groups.filter(group => group.label !== "Testing");
  }
  return groups;
}

export const NAV_GROUPS: NavGroup[] = filterNavGroups(ALL_NAV_GROUPS);
export const MOBILE_NAV_GROUPS: NavGroup[] = filterNavGroups(ALL_MOBILE_NAV_GROUPS);
